import React, { useId, useMemo, useState } from 'react';

import type { PopoverProps } from '@mui/material';
import { List, ListItemButton, Popover, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { uniqBy } from 'ramda';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SearchInput } from 'src/components';
import { AlineCheckbox, AlineTooltip } from 'src/components/generic';
import { SkinnyPlusIcon } from 'src/components/icons/SkinnyPlusIcon';
import { useSelectCurrentUser } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import type { WorkflowFileSubset } from 'src/hooks';
import {
  useCreateParty,
  useGetCheckWorkflowAuthorization,
  usePartySearch,
  useSetWorkflowParties,
} from 'src/hooks';
import type { UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import {
  labelPopoverMaxHeightPx,
  labelPopoverWidthPx,
  smallCheckboxHeightPx,
  smallCheckboxWidthPx,
} from 'src/workflow/labels';
import { makeStyles } from 'tss-react/mui';

import { labelPopoverOffsetPx } from './workflow-list';

export type TextColorType =
  | 'initial'
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'textPrimary'
  | 'textSecondary'
  | 'error';

const numDisplayedParties = 4;
const useStyles = makeStyles()(theme => ({
  ghostPartyText: {
    '&:hover': {
      color: theme.palette.primary.main,
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
    },
    cursor: 'pointer',
    textDecorationLine: 'underline',
    textDecorationStyle: 'dashed',
  },
  partyName: {
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    cursor: 'pointer',
  },
  partyNameList: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
  },
  popover: {
    padding: theme.spacing(2),
    width: labelPopoverWidthPx,
  },
  spacingRight: {
    marginRight: theme.spacing(0.5),
  },
}));

interface PartiesListBodyProps {
  parties: PartySubset[];
  setOpen: (open: boolean) => void;
  textColor?: TextColorType;
}

export function PartiesListBody({
  parties,
  setOpen,
  textColor = 'textSecondary',
}: PartiesListBodyProps) {
  const { classes } = useStyles();
  const sortedParties = [...parties].sort((a, b) => a.name.localeCompare(b.name));
  const displayedParties = sortedParties.slice(0, numDisplayedParties);
  const remainingCount = Math.max(0, parties.length - numDisplayedParties);

  if (!parties.length) {
    return (
      <Typography
        noWrap
        className={classes.ghostPartyText}
        color={textColor}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Add Party +
      </Typography>
    );
  }

  return (
    <>
      {displayedParties.map((party, index) => {
        const isLast = index === displayedParties.length - 1 && remainingCount === 0;

        return (
          <React.Fragment key={party.id}>
            <Typography
              noWrap
              className={clsx(classes.partyName, !isLast && classes.spacingRight)}
              color={textColor}
              variant="body2"
              onClick={e => {
                e.stopPropagation();
                setOpen(true);
              }}
            >
              {isLast ? party.name : `${party.name},`}
            </Typography>
          </React.Fragment>
        );
      })}
      {remainingCount > 0 && (
        <Typography
          noWrap
          className={classes.partyName}
          color={textColor}
          variant="body2"
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          + {remainingCount} {remainingCount === 1 ? 'party' : 'parties'}
        </Typography>
      )}
    </>
  );
}

interface WorkflowWithParties extends Pick<Apollo.Workflow, 'id' | '__typename'> {
  files: WorkflowFileSubset[];
  owner: UserWithOrg;
  parties: PartySubset[];
  permissions: WorkflowPermissionSubset[];
}

export function WorkflowPartiesList({
  textColor = 'textSecondary',
  workflow,
}: {
  workflow: WorkflowWithParties;
  textColor?: TextColorType;
}) {
  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <div ref={setAnchorEl} className={classes.partyNameList}>
      <PartiesListBody parties={workflow.parties} setOpen={setOpen} textColor={textColor} />
      <WorkflowPartiesPopover
        anchorEl={anchorEl}
        open={open}
        setOpen={setOpen}
        workflow={workflow}
      />
    </div>
  );
}

export function WorkflowPartiesPopover({
  anchorEl,
  open,
  setOpen,
  workflow,
}: {
  anchorEl: Element | null;
  open: boolean;
  setOpen: (value: boolean) => void;
  workflow: WorkflowWithParties;
}) {
  const currentUser = useSelectCurrentUser();
  const { setWorkflowParties } = useSetWorkflowParties();
  const { checkWorkflowAuthorization } = useGetCheckWorkflowAuthorization();
  const { canEditWorkflow } = checkWorkflowAuthorization(workflow, currentUser);

  async function handlePartyToggle(party: PartySubset) {
    const newPartyIds = new Set(workflow.parties.map(p => p.id));

    if (newPartyIds.has(party.id)) newPartyIds.delete(party.id);
    else newPartyIds.add(party.id);

    await setWorkflowParties({
      files: workflow.files,
      parties: uniqBy(
        p => p.id,
        [...workflow.parties, party].filter(p => newPartyIds.has(p.id))
      ),
      workflowId: workflow.id,
    });
  }

  return (
    <PartiesEditor
      disabledReason={
        !canEditWorkflow
          ? 'Only the workflow owner and editors can edit parties'
          : !workflow.files.length
            ? 'No documents added to workflow'
            : ''
      }
      popoverProps={{
        anchorEl,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        onClose: () => setOpen(false),
        open,
        style: { transform: `translateX(${labelPopoverOffsetPx}px)` },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
      }}
      selectedParties={workflow.parties}
      onPartyToggle={handlePartyToggle}
    />
  );
}

const usePartyEditorStyles = makeStyles<{ hasParties: boolean }, 'listItem'>()(
  (theme, { hasParties }, classes) => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: labelPopoverMaxHeightPx,
      overflow: 'hidden',
    },
    footer: {
      borderTop: hasParties ? `1px solid ${theme.palette.divider}` : undefined,
      padding: theme.spacing(1),
    },
    ghostCheckbox: {
      alignItems: 'center',
      display: 'flex',
      height: smallCheckboxHeightPx,
      justifyContent: 'center',
      width: smallCheckboxWidthPx,
    },
    hiddenCheckbox: {
      [`.${classes.listItem}:hover &, input:checked ~ *`]: {
        visibility: 'visible',
      },
      boxSizing: 'border-box',
      height: smallCheckboxHeightPx,
      visibility: 'hidden',
      width: smallCheckboxWidthPx,
    },
    labelList: {
      padding: theme.spacing(1),
    },
    listItem: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1),
    },
    listScroll: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
    },
    paper: {
      maxHeight: labelPopoverMaxHeightPx,
      width: labelPopoverWidthPx,
    },
    searchContainer: {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
      padding: theme.spacing(1),
    },
  })
);

export type PartySubset = Pick<Apollo.Party, 'id' | 'name' | '__typename'>;
export interface PartyEditorPopoverProps extends Required<
  Pick<PopoverProps, 'anchorEl' | 'anchorOrigin' | 'onClose' | 'open' | 'transformOrigin'>
> {
  style?: React.CSSProperties;
}
interface PartiesEditorProps {
  onPartyToggle: (party: PartySubset) => Promise<void>;
  popoverProps: PartyEditorPopoverProps;
  selectedParties: PartySubset[];
  disabledReason?: string;
}

/** Placeholder rows shown while parties load (initial fetch and load-more). */
function PartyRowSkeletons({
  count,
  rowClassName,
}: {
  count: number;
  rowClassName: string;
}): JSX.Element {
  return (
    <>
      {Array.from({ length: count }, (_unused, index) => (
        <div key={index} className={rowClassName}>
          <Skeleton variant="text" width="70%" />
        </div>
      ))}
    </>
  );
}

export function PartiesEditor({
  disabledReason = '',
  onPartyToggle,
  popoverProps,
  selectedParties,
}: PartiesEditorProps) {
  const {
    hasMore,
    loadMore,
    loading,
    parties: sortedParties,
    refetch: refetchParties,
    search: searchText,
    setSearch: setSearchText,
  } = usePartySearch({ skip: !popoverProps.open });
  const partyListScrollId = `party-popover-${useId().replace(/:/g, '')}`;
  const { createParty } = useCreateParty();
  const selectedPartyIds = useMemo(
    () => new Set(selectedParties.map(party => party.id)),
    [selectedParties]
  );
  const isDuplicateParty = useMemo(() => {
    const trimmed = searchText.trim().toLowerCase();
    return sortedParties.some(party => party.name.toLowerCase() === trimmed);
  }, [sortedParties, searchText]);
  const hasParties = Boolean(sortedParties.length);
  const { classes } = usePartyEditorStyles({ hasParties });
  const isDisabled = Boolean(disabledReason);

  async function handleCreateParty() {
    /**
     * `isDuplicateParty` is computed against the loaded (paginated) search
     * results, so block creation while a search is still in flight to avoid
     * creating a duplicate of a party that simply hasn't loaded yet.
     */
    if (isDisabled || loading || !searchText.trim() || isDuplicateParty) return;

    const { data } = await createParty({ variables: { input: { props: { name: searchText } } } });
    if (!data?.createParty) return;

    await onPartyToggle(data.createParty);
    // The search results come from a separate query, so surface the new party.
    refetchParties();
  }

  return (
    <Popover
      classes={{ paper: classes.paper }}
      onClick={e => e.stopPropagation()}
      {...popoverProps}
    >
      <div className={classes.container}>
        <div className={classes.searchContainer}>
          <SearchInput
            placeholder="Search or add parties..."
            search={searchText}
            setSearch={setSearchText}
          />
        </div>
        {hasParties && (
          <div className={classes.listScroll} id={partyListScrollId}>
            <InfiniteScroll
              dataLength={sortedParties.length}
              hasMore={hasMore}
              loader={<PartyRowSkeletons count={1} rowClassName={classes.listItem} />}
              next={loadMore}
              scrollableTarget={partyListScrollId}
            >
              <List className={classes.labelList}>
                {sortedParties.map(party => (
                  <AlineTooltip key={party.id} arrow placement="top" title={disabledReason}>
                    <div>
                      <ListItemButton
                        className={classes.listItem}
                        disabled={isDisabled}
                        onClick={async () => {
                          await onPartyToggle(party);
                        }}
                      >
                        <AlineCheckbox
                          checked={selectedPartyIds.has(party.id)}
                          className={classes.hiddenCheckbox}
                          disabled={isDisabled}
                          size="small"
                        />
                        <Typography noWrap>{party.name}</Typography>
                      </ListItemButton>
                    </div>
                  </AlineTooltip>
                ))}
              </List>
            </InfiniteScroll>
          </div>
        )}
        {loading && !hasParties && (
          <List className={classes.labelList}>
            <PartyRowSkeletons count={5} rowClassName={classes.listItem} />
          </List>
        )}
        {searchText && !isDuplicateParty && !loading && (
          <div className={classes.footer}>
            <AlineTooltip arrow placement="top" title={disabledReason}>
              <div>
                <ListItemButton
                  className={classes.listItem}
                  disabled={isDisabled}
                  onClick={handleCreateParty}
                >
                  <div className={classes.ghostCheckbox}>
                    <SkinnyPlusIcon />
                  </div>
                  <Typography noWrap>{`Create "${searchText}" party`}</Typography>
                </ListItemButton>
              </div>
            </AlineTooltip>
          </div>
        )}
      </div>
    </Popover>
  );
}
